import React, { useEffect } from 'react';
import { Space, Switch } from 'antd';
import { useSelector } from 'react-redux';
import { useAtom, useAtomValue } from 'jotai';
import { aiAlertAtom, annotationAtom, manualAlertAtom } from '@src/atoms/toggles';
import { track } from '@utils';
import { CLICK_DATE_COMPARISON } from '@src/utils/sensor/track';
import { annotationsFeatureSelector, realTimeModeOnTrendsSelector } from '@store/common/selectors';
import { appAIAlertEnabledSelector } from '@utils/appInsights';
import { isAppModule } from '@utils/constants';
import { prefixedCheckFeature } from '@utils/platform/features/checkFeature';
import { Header, ToggleText } from './styled';
import {
  dodAtom,
  activeTabAtom as metricPanelActiveTabAtom,
  Tabs as metricPanelTabs,
} from '@src/components/metrics-panel-hz/atoms';

const cf = prefixedCheckFeature('trends.toggles');

function Toggles() {
  const [manual, setManual] = useAtom(manualAlertAtom);
  const [ai, setAi] = useAtom(aiAlertAtom);
  const [anno, setAnno] = useAtom(annotationAtom);
  const [dod, setDod] = useAtom(dodAtom);
  const isAnnotationEnabled = useSelector(annotationsFeatureSelector);
  const activeMetricPanelTab = useAtomValue(metricPanelActiveTabAtom);
  const liveModeSelected: boolean = useSelector(realTimeModeOnTrendsSelector);
  const isAppAlertEnabled = useSelector(appAIAlertEnabledSelector);

  const isWidgetTabActive = activeMetricPanelTab === metricPanelTabs.Widgets;

  const onToggleCheck = (setToggle) => (checked) => setToggle(checked);

  const onDodToggle = (checked) => {
    setDod(checked);
    track({ event: CLICK_DATE_COMPARISON });
  };

  // disable on live mode
  useEffect(() => {
    if (liveModeSelected) {
      setDod(false);
      setAi(false);
      setManual(false);
    }
  }, [liveModeSelected]);

  const disabledForAppPOC = !isAppAlertEnabled && isAppModule;

  return (
    <Space direction="vertical">
      <Header>View in Diagnostics</Header>
      <div>
        <Switch
          size="small"
          checked={manual}
          onChange={onToggleCheck(setManual)}
          disabled={
            isAppModule || liveModeSelected || isWidgetTabActive || !cf('manual-alerts', true)
          }
        />
        <ToggleText>Manual Alerts</ToggleText>
      </div>
      <div>
        <Switch
          size="small"
          checked={ai}
          onChange={onToggleCheck(setAi)}
          disabled={
            disabledForAppPOC || liveModeSelected || isWidgetTabActive || !cf('ai-alerts', true)
          }
        />
        <ToggleText>AI Alerts</ToggleText>
      </div>
      <div>
        <Switch
          size="small"
          checked={anno && isAnnotationEnabled}
          onChange={onToggleCheck(setAnno)}
          disabled={!isAnnotationEnabled || isWidgetTabActive || !cf('annotations', true)}
        />
        <ToggleText>Annotations</ToggleText>
      </div>
      <div>
        <Switch
          size="small"
          checked={dod}
          onChange={onDodToggle}
          disabled={liveModeSelected || isWidgetTabActive || !cf('dod', true)}
        />
        <ToggleText> Date Comparison</ToggleText>
      </div>
    </Space>
  );
}

export default Toggles;
